import CheckIcon from '@mui/icons-material/Check'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import WhatsappIcon from '@mui/icons-material/WhatsApp'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

import Tooltip from '@/app/component/atom/tooltip'
import { DataMap } from '@/app/module/campaigns/types'
import { TGetFileFunction } from '@/app/module/conversations/types'
import { getConversationMessageStatus } from '@/app/module/logs/message-status'
import { formatDateTime } from '@/app/service/util'
import { SMSLog, WhatsAppLog } from '@/app/types/log'

import SMS from './sms'
import Voice from './voice'
import WhatsappConversationPart from './whatsapp'

type Props = {
  part: WhatsAppLog | SMSLog
  type: string
  getFile: TGetFileFunction
}

const componentMap: DataMap = {
  sms: SMS,
  voice: Voice,
  whatsapp: WhatsappConversationPart,
}

const colorMap: DataMap = {
  sms: {
    egress: {
      backgroundColor: 'rgba(47, 172, 91, 0.3)',
      color: 'rgba(0,0,0,0.75)',
    },
    ingress: {
      backgroundColor: 'rgba(47, 128, 172, 0.3)',
      color: 'rgba(0,0,0,0.75)',
    },
  },
  whatsapp: {
    egress: {
      backgroundColor: 'rgba(47, 172, 91, 0.3)',
      color: 'rgba(0,0,0,0.75)',
    },
    ingress: {
      backgroundColor: 'rgba(47, 128, 172, 0.3)',
      color: 'rgba(0,0,0,0.75)',
    },
  },
  voice: {
    egress: {
      backgroundColor: 'rgba(47, 172, 91, 0.3)',
      color: 'rgba(0,0,0,0.75)',
    },
    ingress: {
      backgroundColor: 'rgba(47, 128, 172, 0.3)',
      color: 'rgba(0,0,0,0.75)',
    },
  },
}

const iconMap: DataMap = {
  sms: <Icon fontSize="small">sms</Icon>,
  whatsapp: <WhatsappIcon sx={{ width: '20px', height: '20px' }} />,
  voice: <Icon fontSize="small">phone_enabled</Icon>,
}

const renderStatusIcon = (status: string) => {
  if (!status) {
    return null
  }

  if (status === 'Read') {
    return <DoneAllIcon color="primary" fontSize="inherit" />
  }
  if (status === 'Delivered') {
    return <DoneAllIcon fontSize="inherit" />
  }
  if (status === 'Sent') {
    return <CheckIcon fontSize="inherit" />
  }
  if (status === 'Pending' || status === 'In Progress') {
    return <HourglassEmptyIcon fontSize="inherit" />
  }
  if (status === 'Not Sent') {
    return <RadioButtonUncheckedIcon fontSize="inherit" />
  }
  if (status.startsWith('Failed') || status.startsWith('Not Delivered') || status.startsWith('Not Sent')) {
    return <ErrorOutlineIcon color="error" fontSize="inherit" />
  }
  return <RadioButtonUncheckedIcon fontSize="inherit" />
}

const Part: React.FC<Props> = (props) => {
  const { part, type, getFile } = props
  const Component: any = componentMap[type]
  const senderId = part.direction === 'egress' ? part.sender : part.recipient
  const messageStatus = part.direction === 'egress' ? getConversationMessageStatus(part) : ''
  return (
    <Container
      className={`conversations-part-wrapper ${
        part.direction === 'egress' ? 'conversations-part-egress' : 'conversations-part-ingress'
      }`}
    >
      <div
        style={{
          fontSize: '14px',
          display: 'flex',
          marginBottom: '10px',
          ...(part.direction === 'egress' && {
            flexDirection: 'row-reverse',
          }),
        }}
      >
        <div
          style={{
            alignSelf: 'flex-end',
            ...(part.direction === 'ingress' && {
              transform: 'scaleX(-1)',
              paddingLeft: '2px',
            }),
          }}
        >
          {iconMap[type]}
        </div>
        <Arrow
          style={{
            borderLeft: `3px solid ${colorMap[type][part.direction].backgroundColor}`,
            ...(part.direction === 'ingress' && {
              transform: 'scaleX(-1)',
            }),
          }}
        />
        <Card
          style={{
            ...colorMap[type][part.direction],
            ...(part.direction === 'egress' && {
              textAlign: 'right',
              borderBottomRightRadius: '0px',
              borderBottomLeftRadius: '4px',
            }),
          }}
        >
          <CardContent>
            <Component getFile={getFile} part={part} />
            <div
              style={{
                display: 'flex',
                justifyContent: part.direction === 'egress' ? 'flex-end' : 'flex-start',
              }}
            >
              <CaptionTextContainer variant="caption" color="textSecondary">
                {part.direction === 'egress' ? 'From' : 'To'} <CaptionText>{senderId}</CaptionText> at{' '}
                <CaptionText>{formatDateTime(part.created || '')}</CaptionText>
                {messageStatus && (
                  <Tooltip title={messageStatus}>
                    <StatusIconWrapper aria-label={messageStatus} role="img">
                      {renderStatusIcon(messageStatus)}
                    </StatusIconWrapper>
                  </Tooltip>
                )}
              </CaptionTextContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </Container>
  )
}

const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}))

const CardContent = styled('div')(() => ({
  padding: '10px',
}))

const Arrow = styled('div')(() => ({
  width: 0,
  height: 0,
  borderTop: '7px solid transparent',
  alignSelf: 'flex-end',
}))

const Card = styled('div')(() => ({
  maxWidth: '65%',
  textAlign: 'left',
  borderRadius: '4px',
  borderBottomLeftRadius: '0px',
}))

const CaptionTextContainer = styled(Typography)(() => ({
  fontSize: '0.60rem',
}))

const CaptionText = styled('span')(() => ({
  fontSize: '0.65rem',
}))

const StatusIconWrapper = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: '4px',
  fontSize: '0.85rem',
  verticalAlign: 'text-bottom',
}))

export default React.memo(Part)
